import { useMemo, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import Container from "../../components/container/Container";
import {
	Button,
	Text,
	Modal,
	TagSelector,
} from "@adoptaunabuelo/react-components";
import FelicitacionStepHeader from "../../components/felicitacion/StepHeader";
import { felicitacionSteps, felicitacionTotalSteps } from "./steps/config";
import { Constant, UserScheme } from "client-v2";
import grandpaDefaultImage from "../../assets/images/icon/grandpa_icon.png";
import grandmaDefaultImage from "../../assets/images/icon/grandma_icon.png";
import { Cake, MapPin, X } from "lucide-react";

interface Props {
	step: number;
	onNext: () => void;
	onBack: () => void;
	selectedGrandpa?: UserScheme;
	onOpenProfile: () => void;
}

const Page = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 100%;
	height: 100dvh;
	min-height: 100vh;
	overflow: hidden;
	overflow-x: hidden;
	background: var(--surface-background, #f9f6f3);
`;

const HeaderShell = styled.div`
	flex-shrink: 0;
	width: 100%;
	max-width: 100%;
	background: var(--surface-background, #f9f6f3);
`;

const Content = styled.div`
	display: flex;
	flex: 1;
	width: 100%;
	max-width: 100%;
	min-height: 0;
	min-width: 0;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-start;
	overflow: hidden;
	overflow-x: hidden;
	margin-top: -8px;
	box-sizing: border-box;
	padding: 0 0 24px;
	${media.lessThan("medium")`
		padding: 0 0 calc(100px + env(safe-area-inset-bottom, 0px));
	`}
	@media (max-width: 400px) {
		padding: 0 16px calc(100px + env(safe-area-inset-bottom, 0px));
	}
	@media (max-width: 360px) {
		padding: 0 12px calc(96px + env(safe-area-inset-bottom, 0px));
	}
`;

const StepWrapper = styled.div`
	width: 100%;
	max-width: 720px;
	align-self: center;
	flex: 1;
	min-height: 0;
	min-width: 0;
	box-sizing: border-box;
	background: var(--surface-background, #f9f6f3);
	border-radius: 32px;
	overflow: hidden;
	overflow-x: hidden;
`;

const DesktopAction = styled.div`
	display: none;

	${media.greaterThan("medium")`
		display: flex;
		width: 100%;
		max-width: 720px;
		align-self: center;
		justify-content: flex-end;
		padding: 16px 0 0;
	`}
`;

const FooterProfile = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0;
	border: 0;
	background: transparent;
	text-align: left;
	cursor: pointer;
`;

const FooterAvatar = styled.img`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
	@media (max-width: 360px) {
		width: 32px;
		height: 32px;
	}
`;

const FooterCopy = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	min-width: 0;
	padding-right: 8px;
`;

const ModalContent = styled.div`
	position: relative;
	padding: 12px 0px 24px 0px;
	border-radius: 40px 40px 0 0;
`;

const ModalHandle = styled.div`
	width: 80px;
	height: 4px;
	border-radius: 999px;
	background: rgba(0, 29, 61, 0.2);
	margin: 0 auto 24px;
`;

const ModalClose = styled.button`
	position: absolute;
	top: 16px;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #001d3d;
	cursor: pointer;
`;

const ModalHeader = styled.div`
	padding-bottom: 16px;
`;

const ModalSummary = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 120px;
	gap: 8px;
	align-items: start;
`;

const ModalImage = styled.img`
	width: 120px;
	height: 120px;
	object-fit: cover;
	border-radius: 24px;
`;

const ModalMeta = styled.div`
	display: flex;
	min-width: 0;
	flex-direction: column;
	gap: 8px;
`;

const MetaItem = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 4px 0;
`;

const ModalName = styled.div`
	padding-bottom: 8px;
`;

const MetaText = styled.div`
	display: flex;
	min-width: 0;
	flex-direction: column;
	gap: 4px;
`;

const ModalSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
	padding: 24px 0px 0px 0px;
`;

const HobbiesContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;

	& > * {
		margin: 0 !important;
	}
`;

const LinkButton = styled.button`
	border: 0;
	padding: 0;
	width: fit-content;
	background: transparent;
	color: #001d3d;
	font: inherit;
	font-weight: 600;
	text-decoration: underline;
	cursor: pointer;
`;

const getFormattedBirthday = (iso?: string) => {
	if (!iso) return "Fecha pendiente";

	return new Intl.DateTimeFormat("es-ES", {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(new Date(iso));
};

const getAgeLabel = (iso?: string) => {
	if (!iso) return "Edad pendiente";

	const birthDate = new Date(iso);
	const today = new Date();
	let age = today.getFullYear() - birthDate.getFullYear();
	const monthDiff = today.getMonth() - birthDate.getMonth();

	if (
		monthDiff < 0 ||
		(monthDiff === 0 && today.getDate() < birthDate.getDate())
	) {
		age -= 1;
	}

	return `${age} años`;
};

const getGrandpaStory = (selectedGrandpa?: UserScheme) => {
	if (!selectedGrandpa) return "Estamos preparando más información.";
	if (typeof selectedGrandpa.live?.history === "string") {
		return selectedGrandpa.live.history;
	}

	const hobbies = Array.isArray(selectedGrandpa.data?.hobbies)
		? (selectedGrandpa.data?.hobbies ?? [])
		: [];
	const shortAddress =
		selectedGrandpa.shortAddress ??
		selectedGrandpa.address ??
		"su localidad";
	const hobbyText = hobbies.length
		? `Le gusta especialmente ${hobbies.slice(0, 3).join(", ")}.`
		: "Estamos preparando más detalles sobre sus aficiones.";

	return `${selectedGrandpa.name ?? "Esta persona mayor"} vive en ${shortAddress}. ${hobbyText}`;
};

const STORY_PREVIEW_LENGTH = 180;

const FelicitacionDetail = ({
	step,
	onNext,
	onBack,
	selectedGrandpa,
}: Props) => {
	const [showProfile, setShowProfile] = useState(false);
	const [showFullStory, setShowFullStory] = useState(false);
	const currentStep = Math.min(Math.max(1, step), felicitacionTotalSteps);
	const isLast = currentStep >= felicitacionTotalSteps;
	const {
		Component: StepComponent,
		imageSrc,
		imageAlt,
	} = felicitacionSteps[currentStep - 1];
	const isFirstStep = currentStep === 1;
	const profileImageSrc =
		typeof selectedGrandpa?.image === "string"
			? selectedGrandpa.image
			: (selectedGrandpa?.image?.url ??
				(selectedGrandpa?.gender === "female"
					? grandmaDefaultImage
					: grandpaDefaultImage));
	const hobbies = useMemo(() => {
		const ids = Array.isArray(selectedGrandpa?.data?.hobbies)
			? (selectedGrandpa?.data?.hobbies ?? [])
			: [];
		return Constant.hobbies().filter((hobby) => ids.includes(hobby.id));
	}, [selectedGrandpa]);
	const story = getGrandpaStory(selectedGrandpa);
	const hasLongStory = story.length > STORY_PREVIEW_LENGTH;
	const visibleStory =
		hasLongStory && !showFullStory
			? `${story.slice(0, STORY_PREVIEW_LENGTH).trimEnd()}...`
			: story;

	return (
		<>
			<Container
				contentStyle={{ padding: "0px" }}
				headerProps={{ hideLogo: true, style: { display: "none" } }}
				footerProps={{
					style: {
						borderTop: "0px solid transparent",
						padding: "16px 14px"
					},
					childrenPosition: isFirstStep ? "top" : "left",
					children:
						!isFirstStep && selectedGrandpa ? (
							<FooterProfile
								onClick={() => {
									setShowFullStory(false);
									setShowProfile(true);
								}}
								type="button"
							>
								<FooterAvatar
									alt={selectedGrandpa?.name ?? ""}
									src={profileImageSrc}
								/>
								<FooterCopy>
									<Text type="b2" weight="regular">
										¿Se te olvidó algo sobre{" "}
										{selectedGrandpa.name}?{" "}
										<span style={{ fontWeight: 600 }}>
											Ver perfil
										</span>
									</Text>
								</FooterCopy>
							</FooterProfile>
						) : undefined,
					buttonProps: {
						children: isLast ? "Finalizar" : "Siguiente",
						style: {
							width: isFirstStep ? "100%" : undefined,
							minWidth: isFirstStep ? undefined : 110,
						},
						onClick: onNext,
					},
				}}
			>
				<Page>
					<HeaderShell>
						<FelicitacionStepHeader
							imageAlt={imageAlt}
							imageSrc={imageSrc}
							onBack={onBack}
							step={currentStep}
							title="Cómo enviar tu felicitación"
						/>
					</HeaderShell>

					<Content>
						<StepWrapper>
							<StepComponent selectedGrandpa={selectedGrandpa} />
						</StepWrapper>

						<DesktopAction>
							<Button
								onClick={onNext}
								style={{ minWidth: 110 }}
							>
								{isLast ? "Finalizar" : "Siguiente"}
							</Button>
						</DesktopAction>
					</Content>
				</Page>
			</Container>

			<Modal
				type="full-screen"
				isVisible={showProfile}
				onClose={() => {
					setShowProfile(false);
					setShowFullStory(false);
				}}
				hideClose={true}
				hideHeader={true}
				shouldCloseOnOverlayClick={true}
			>
				{selectedGrandpa && (
					<ModalContent>
						<ModalHeader>
							<ModalHandle />
							<ModalClose
								onClick={() => {
									setShowProfile(false);
									setShowFullStory(false);
								}}
								type="button"
							>
								<X size={28} strokeWidth={2.2} />
							</ModalClose>
						</ModalHeader>
						<ModalName>
							<Text type="h3" weight="semibold">
								{selectedGrandpa.name}
							</Text>
						</ModalName>

						<ModalSummary>
							<ModalMeta>
								<MetaItem>
									<Cake
										color="#0A84E3"
										size={20}
										strokeWidth={2.2}
									/>
									<MetaText>
										<Text type="p">
											{getFormattedBirthday(
												selectedGrandpa.birthday?.iso,
											)}
										</Text>
										<Text type="p" weight="semibold">
											{getAgeLabel(
												selectedGrandpa.birthday?.iso,
											)}
										</Text>
									</MetaText>
								</MetaItem>

								<MetaItem>
									<MapPin
										color="#0A84E3"
										size={20}
										strokeWidth={2.2}
									/>
									<MetaText>
										<Text type="p">
											{selectedGrandpa.shortAddress ??
												selectedGrandpa.address ??
												"Ubicación pendiente"}
										</Text>
									</MetaText>
								</MetaItem>
							</ModalMeta>

							<ModalImage
								alt={selectedGrandpa.name ?? ""}
								src={profileImageSrc}
							/>
						</ModalSummary>

						<ModalSection>
							<Text type="h6" weight="semibold">
								Su historia
							</Text>
							<Text type="p">{visibleStory}</Text>
							{hasLongStory && !showFullStory && (
								<LinkButton
									onClick={() => setShowFullStory(true)}
									type="button"
								>
									Leer más
								</LinkButton>
							)}
						</ModalSection>

						{!!hobbies.length && (
							<ModalSection>
								<Text type="h6" weight="semibold">
									Aficiones
								</Text>
								<HobbiesContainer>
									{hobbies.map((hobby) => (
										<TagSelector
											design="design2"
											key={hobby.id}
											options={[hobby]}
											onlyVisual={true}
										/>
									))}
								</HobbiesContainer>
							</ModalSection>
						)}
					</ModalContent>
				)}
			</Modal>
		</>
	);
};

export default FelicitacionDetail;
